import { html } from 'lit';
import { COORDINATE_SYSTEMS, HEIGHT_DATUMS, DEFAULT_COORDINATE_SYSTEM, DEFAULT_HEIGHT_DATUM,
         SELECTABLE_SOIL_STANDARDS, DEFAULT_SOIL_STANDARD, nen6693 } from '@mv/models';
import { tijdzoneVeld } from './tijdzone-veld.js';

/**
 * De velden van een project, één keer beschreven.
 *
 * Je komt bij dit formulier langs twee wegen: het potloodje in het projectenoverzicht en het
 * potloodje binnen een project. Dat waren twee kopieën in twee componenten met elk hun eigen
 * stylesheet, en ze liepen uit elkaar -- in de ene stonden de keuzelijsten wit (`--mv-surface`
 * inline) en in de andere grijs (`--mv-surface-alt`). Zo'n verschil valt pas op in een
 * screenshot, en de volgende wijziging landt weer in één van de twee.
 *
 * De opmaak staat daarom bij de velden zelf en niet in een stylesheet: dit blok wordt gerenderd
 * binnen twee verschillende shadow roots, en dan is meeliftende CSS juist de bron van het
 * verschil.
 *
 * De aanroeper houdt zijn eigen toestand en zijn eigen opslaan-logica; dit levert alleen de
 * velden en roept `zet(sleutel, waarde)` als er iets verandert.
 *
 * @param {object} o
 * @param {object} o.waarden            huidige waarden (name, code, parentId, …)
 * @param {(k: string, v: any) => void} o.zet  wordt geroepen bij elke wijziging
 * @param {Array|null} o.hoofdprojecten mogelijke hoofdprojecten; leeg/niet meegegeven = veld weg
 * @param {Array} o.sjablonen           beschikbare sjablonen
 * @param {string} o.sjabloonInitieel   het sjabloon waarmee het venster openging (voor de waarschuwing)
 * @param {boolean} o.sjabloonAanmaken  mag deze gebruiker een sjabloon aanmaken?
 * @param {Function} o.opSjabloon       eigen afhandeling van de sjabloonkeuze (incl. '➕ nieuw')
 * @param {Function} o.opStandaard      eigen afhandeling van de boorprofielkeuze (bevestiging bij wisselen)
 */
export function projectVelden({
  waarden, zet, hoofdprojecten = null, sjablonen = [], sjabloonInitieel = '',
  sjabloonAanmaken = false, opSjabloon = null, opStandaard = null,
}) {
  // Eén schrijfwijze voor elk veld. Grijze vulling: in een wit venster is een veld zonder
  // achtergrond alleen aan zijn dunne rand te herkennen.
  const VELD = 'width:100%; box-sizing:border-box; border:1px solid var(--mv-border); border-radius:6px;'
    + ' padding:10px; font-family:inherit; font-size:0.9rem;'
    + ' background:var(--mv-surface-alt,#F3F4F6); color:var(--mv-text);';
  const w = waarden || {};

  return html`
    <div class="form-group">
      <label for="pv_name" class="form-label">Projectnaam *</label>
      <input type="text" id="pv_name" style=${VELD} required
        placeholder="Voer projectnaam in..."
        .value=${w.name || ''} @input=${(e) => zet('name', e.target.value)}>
    </div>

    <div class="form-group">
      <label for="pv_code" class="form-label">Projectcode (optioneel)</label>
      <input type="text" id="pv_code" style=${VELD}
        placeholder="Bijv. PRJ-2024-001"
        .value=${w.code || ''} @input=${(e) => zet('code', e.target.value)}>
    </div>

    ${!hoofdprojecten ? '' : html`
      <div class="form-group">
        <label for="pv_parent" class="form-label">Hoofdproject (optioneel)</label>
        <select id="pv_parent" style=${VELD}
          .value=${w.parentId || ''} @change=${(e) => zet('parentId', e.target.value)}>
          <option value="">-- Geen (dit is een hoofdproject) --</option>
          ${hoofdprojecten.map((p) => html`
            <option value=${p.id} ?selected=${String(p.id) === String(w.parentId)}>${p.project_name || p.name || p.id}</option>`)}
        </select>
      </div>`}

    <div class="form-group">
      <label for="pv_coord" class="form-label">Coördinaatsysteem</label>
      <select id="pv_coord" style=${VELD}
        .value=${w.coordinateSystem || DEFAULT_COORDINATE_SYSTEM}
        @change=${(e) => zet('coordinateSystem', e.target.value)}>
        ${Object.values(COORDINATE_SYSTEMS).map((s) => html`
          <option value=${s.id} ?selected=${s.id === (w.coordinateSystem || DEFAULT_COORDINATE_SYSTEM)}>${s.label}</option>`)}
      </select>
    </div>

    <div class="form-group">
      <label for="pv_height" class="form-label">Hoogtesysteem</label>
      <select id="pv_height" style=${VELD}
        .value=${w.heightDatum || DEFAULT_HEIGHT_DATUM}
        @change=${(e) => zet('heightDatum', e.target.value)}>
        ${Object.values(HEIGHT_DATUMS).map((s) => html`
          <option value=${s.id} ?selected=${s.id === (w.heightDatum || DEFAULT_HEIGHT_DATUM)}>${s.label}</option>`)}
      </select>
    </div>

    <div class="form-group">
      <label for="pv_standard" class="form-label">Standaard boorprofiel</label>
      <select id="pv_standard" style=${VELD}
        .value=${w.soilStandard || DEFAULT_SOIL_STANDARD}
        @change=${(e) => (opStandaard ? opStandaard(e) : zet('soilStandard', e.target.value))}>
        ${SELECTABLE_SOIL_STANDARDS.map((s) => html`
          <option value=${s.id} ?selected=${s.id === (w.soilStandard || DEFAULT_SOIL_STANDARD)}>${s.label}</option>`)}
      </select>
    </div>

    <div class="form-group">
      <label class="form-label">Korrelverdeling</label>
      <!-- De .form-group-stijlen zijn voor tekstvelden gemaakt en rekken een selectievakje op
           tot de volle breedte met de uitleg buiten beeld; vandaar de resets hier. -->
      <label style="display:flex; align-items:center; gap:10px; cursor:pointer; padding:10px;
                    margin:0; text-transform:none; letter-spacing:normal; font-weight:400;
                    border:1px solid var(--mv-border); border-radius:6px; background:var(--mv-surface-alt,#F3F4F6);">
        <input type="checkbox"
          style="width:16px; height:16px; min-width:16px; padding:0; margin:0; flex:none; accent-color:var(--mv-primary);"
          .checked=${!!w.grainSizeInput} @change=${(e) => zet('grainSizeInput', e.target.checked)}>
        <span style="font-size:0.85rem; color:var(--mv-text); line-height:1.45;">
          Mogelijkheid om percentages op te geven
          <span style="display:block; color:var(--mv-text-muted); font-size:0.8rem; font-weight:400;">
            Per laag optioneel: lutum, leem, grind, silt, organische stof en M50. Vul je ze in, dan volgt de grondsoort daaruit.
          </span>
        </span>
      </label>
    </div>

    ${tijdzoneVeld(w.timezone, (tz) => zet('timezone', tz))}

    <div class="form-group">
      <label for="pv_werksoort" class="form-label">Werksoort onderzoek</label>
      <select id="pv_werksoort" style=${VELD}
        .value=${w.werksoort || ''} @change=${(e) => zet('werksoort', e.target.value)}>
        <option value="">— kies (optioneel) —</option>
        ${nen6693.WERKSOORT.map((x) => html`
          <option value=${x.code} ?selected=${x.code === w.werksoort}>${x.label}</option>`)}
      </select>
      ${!w.werksoort ? '' : html`
        <div style="font-size:0.78rem; color:var(--mv-text-sub,#6c757d); margin-top:4px;">
          NEN 6693-regelset: <strong>${nen6693.WERKSOORT.find((x) => x.code === w.werksoort)?.nenScope}</strong>
        </div>`}
    </div>

    <div class="form-group">
      <label for="pv_template" class="form-label">Sjabloon (optioneel)</label>
      <select id="pv_template" style=${VELD}
        .value=${w.templateId || ''}
        @change=${(e) => (opSjabloon ? opSjabloon(e) : zet('templateId', e.target.value))}>
        <option value="">— Geen sjabloon —</option>
        ${(sjablonen || []).map((t) => html`
          <option value=${t.id} ?selected=${t.id === w.templateId}>${t.name}</option>`)}
        ${sjabloonAanmaken ? html`<option value="__new__">➕ Maak nieuwe sjabloon aan</option>` : ''}
      </select>
      <div style="font-size:0.78rem; color:var(--mv-text-sub,#6c757d); margin-top:4px; line-height:1.5;">
        Een sjabloon vult automatisch standaardvelden voor je in (die je daarna nog kunt aanpassen).
      </div>
      ${w.templateId && w.templateId !== sjabloonInitieel ? html`
        <div style="margin-top:8px; padding:10px 12px; border:1px solid var(--mv-warning-panel-border,#FDE68A); border-radius:8px; background:var(--mv-warning-panel-bg,#FFFBEB); font-size:0.78rem; color:var(--mv-warning-text-sub,#B45309); line-height:1.5;">
          <b>Let op:</b> dit sjabloon geldt alleen voor <b>nieuwe</b> boringen, lagen, potjes en peilbuizen die je hierna in dit project aanmaakt (en voor het automatisch voor-invullen bij het digitaliseren). <b>Bestaande</b> boringen worden niet aangepast, lege velden worden dus niet met terugwerkende kracht ingevuld.
        </div>` : ''}
    </div>

    <div class="form-group">
      <label for="pv_instructions" class="form-label">Werkinstructies (optioneel)</label>
      <textarea id="pv_instructions" placeholder="Voer instructies in voor de veldwerkers..."
        style="${VELD} min-height:100px; resize:vertical;"
        .value=${w.instructions || ''} @input=${(e) => zet('instructions', e.target.value)}></textarea>
    </div>
  `;
}
